import Accordion from "@/lib/Accordion";
import Button from "@/lib/Button";
import useModal from "@/lib/modal/useModal";
import { EmailReceivedEvent, EmailSentEvent } from "@/lib/queries/types";
import dayjs from "dayjs";
import { useParams } from "react-router";
import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import EmailIcon from "~icons/local/email";
import ClearIcon from "~icons/fa6-solid/x";
import { datetimeFormat } from "./utils";

export default function Email({
  data,
}: {
  data: EmailReceivedEvent | EmailSentEvent;
}) {
  const { teamSlug } = useParams();
  const { event_time, message } = data;
  const { from, to, subject, message_id } = message;
  const isReceived = data.type === "email_received";
  const { showModal, closeModal } = useModal();

  return (
    <Accordion
      expanded
      title={
        <div className="flex items-center">
          <EmailIcon />
          <div className="flex items-center gap-1 ml-4">
            <div className="font-bold">Email</div>
            {isReceived ? (
              <div className="text-sm text-slate-500">
                {`from ${from.name} <${from.email}>`}
              </div>
            ) : (
              <div className="text-sm text-slate-500 flex gap-1">
                to{" "}
                {to.map(({ name, email }, i) => (
                  <div key={i}>{`${name} <${email}>`}</div>
                ))}
                on {dayjs(event_time).format(datetimeFormat)}
              </div>
            )}
            <Button
              secondary
              className="ml-1"
              size="sm"
              onClick={() => {
                showModal(
                  <div className="w-[1000px] h-[900px] flex flex-col justify-between gap-4 p-4">
                    <div className="h-8 flex justify-between">
                      <div className="font-semibold text-lg">
                        Original Email
                      </div>
                      <ClearIcon
                        className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                        onClick={closeModal}
                      />
                    </div>
                    <div className="grow overflow-auto">
                      <EmailBody
                        messageId={message_id}
                        teamSlug={teamSlug!}
                        collapse={false}
                      />
                    </div>
                    <div className="flex justify-end items-center">
                      <Button primary onClick={closeModal}>
                        Close
                      </Button>
                    </div>
                  </div>,
                );
              }}
            >
              Open
            </Button>
          </div>
        </div>
      }
    >
      <div className="pl-10 flex flex-col gap-2">
        <div className="flex gap-1 text-sm">
          <div className="text-slate-500">Subject:</div>
          <div>{subject}</div>
        </div>
        <EmailBody
          messageId={message_id}
          teamSlug={teamSlug!}
          collapse={true}
        />
      </div>
    </Accordion>
  );
}

function EmailBody({
  teamSlug,
  messageId,
  collapse,
}: {
  teamSlug: string;
  messageId: string;
  collapse: boolean;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="w-full h-full"
      // @ts-expect-error csp isn't defined on iframe for some reason
      csp="default-src 'none'; img-src data: http: https:; font-src data: http: https:; style-src http: https: 'unsafe-inline'; form-action 'none'; sandbox allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      src={`/api/teams/${teamSlug}/messages/${messageId}/body.html`}
      onLoad={handleIframeLoad}
      style={{ height: contentHeight + "px" }}
    />
  );

  function handleIframeLoad() {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument && iframe.contentWindow) {
      // @ts-expect-error iframe.contentWindow.CSSStyleSheet is not a standard API
      // Remove margin so content height is accurate
      const style = new iframe.contentWindow.CSSStyleSheet();
      style.replaceSync(`
          body { margin: 0 !important; padding: 0 !important; }
      `);
      iframe.contentDocument.adoptedStyleSheets.push(style);

      // Track content height
      const contentBody = iframe.contentDocument.body;
      const observer = new ResizeObserver(() => {
        flushSync(() => {
          setContentHeight(contentBody.scrollHeight);
        });
      });
      observer.observe(contentBody);

      // Find the fold
      const fold = iframe.contentDocument.querySelector(
        "uptool-below-the-fold",
      ) as HTMLDivElement | null;
      if (fold) fold.style.display = collapse ? "none" : "initial";
    }
  }
}
